import { useRef, useEffect } from "react";
import { Html } from "@react-three/drei";
import { gsap } from "gsap";
import type { DialogueLine } from "./useDialogue";

export interface DialogueBubbleProps {
  dialogue: DialogueLine;
  displayedText: string;
  isTyping: boolean;
  showChoices: boolean;
  showInput: boolean;
  inputValue?: string;
  onInputSubmit?: (value: string) => void;
  onAdvance?: () => void;
  error?: string | null;
  position?: [number, number, number];
  rotation?: [number, number, number];
}

/**
 * 3D 공간 상의 말풍선 스타일 대화창
 */
export function DialogueBubble({
  dialogue,
  displayedText,
  isTyping,
  showChoices,
  showInput,
  inputValue = "",
  onInputSubmit,
  onAdvance,
  error,
  position = [0, 2.5, 0],
  rotation = [0, 0, 0],
}: DialogueBubbleProps) {
  const bubbleRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // 입력 필드 자동 포커스
  useEffect(() => {
    if (showInput && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [showInput]);

  // 등장 및 부유 애니메이션
  useEffect(() => {
    if (bubbleRef.current) {
      const tl = gsap.timeline();

      // 등장
      tl.fromTo(
        bubbleRef.current,
        { opacity: 0, scale: 0.9, y: 15 },
        { opacity: 1, scale: 1, y: 0, duration: 0.6, ease: "power3.out" }
      );

      // 부유 (Idle)
      gsap.to(bubbleRef.current, {
        y: "-=8",
        duration: 2,
        repeat: -1,
        yoyo: true,
        ease: "sine.inOut",
        delay: 0.6,
      });
    }
  }, []);

  const handleBubbleClick = () => {
    if (!showChoices && !showInput) {
      onAdvance?.();
    }
  };

  return (
    <Html
      position={position}
      center
      transform
      distanceFactor={8}
      rotation={rotation}
      style={{ pointerEvents: "auto" }}
    >
      <div
        ref={bubbleRef}
        onClick={handleBubbleClick}
        className="cursor-pointer group"
        style={{
          width: "420px",
          padding: "28px",
          // 빈티지 종이 질감 느낌의 그라데이션
          background: "linear-gradient(135deg, #faf8f0 0%, #f5f0e1 100%)",
          border: "1px solid #d4c9b0",
          borderRadius: "12px", // 종이 느낌을 위해 약간 더 각진 느낌
          backdropFilter: "blur(4px)",
          boxShadow: `
            0 10px 30px rgba(0, 0, 0, 0.15),
            inset 0 0 40px rgba(255, 255, 255, 0.5)
          `,
          color: "#2a2a2a", // 어두운 텍스트
          position: "relative",
          fontFamily: "'Noto Serif KR', 'Georgia', serif", // 세리프 폰트로 클래식하게
          transition: "transform 0.3s ease, box-shadow 0.3s ease",
        }}
      >
        {/* 말풍선 꼬리 (종이 느낌) */}
        <div
          style={{
            position: "absolute",
            left: "-20px",
            top: "30px",
            width: "20px",
            height: "30px",
            background: "#f5f0e1",
            // 왼쪽을 향하는 꼬리 모양
            clipPath:
              "path('M 20 0 L 20 30 C 15 25 10 20 0 15 C 10 10 15 5 20 0')",
            filter: "drop-shadow(-2px 0 2px rgba(0,0,0,0.1))",
          }}
        />

        {/* 화자 이름 */}
        <div
          className="text-[11px] font-black mb-3 opacity-60 tracking-[0.2em] uppercase"
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            color: "#8b7355", // 나무/클립보드 포인트 컬러
          }}
        >
          <span className="w-4 h-px bg-[#8b7355] opacity-40"></span>
          {dialogue.speaker}
        </div>

        {/* 대화 텍스트 */}
        <div className="min-h-[70px]">
          <p className="text-[1.1rem] leading-[1.7] font-medium selection:bg-[#8b7355]/20">
            {displayedText}
            {isTyping && (
              <span className="inline-block w-1.5 h-5 bg-[#8b7355]/40 ml-1 rounded-sm animate-pulse align-middle" />
            )}
          </p>
          {error && (
            <p className="mt-3 text-red-600 text-xs font-semibold">{error}</p>
          )}
        </div>

        {/* 입력 필드 - 한글 IME를 위해 완전히 uncontrolled */}
        {showInput && (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              const value = inputRef.current?.value || "";
              onInputSubmit?.(value);
            }}
            className="mt-6 flex gap-3"
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
          >
            <input
              ref={inputRef}
              type={dialogue.showInput === "password" ? "password" : "text"}
              defaultValue={inputValue}
              onKeyDown={(e) => {
                e.stopPropagation();
                // 한글 조합 중에는 Enter 키 무시
                if (e.key === "Enter" && e.nativeEvent.isComposing) {
                  e.preventDefault();
                }
              }}
              placeholder={dialogue.inputPlaceholder || "Type here..."}
              className="flex-1 bg-black/5 border border-[#d4c9b0] px-4 py-2 rounded-lg outline-none focus:bg-white/50 focus:border-[#8b7355] transition-all text-sm text-[#2a2a2a]"
            />
            <button
              type="submit"
              className="px-6 py-2 bg-[#8b7355] hover:bg-[#7a6244] text-white rounded-lg transition-all text-sm font-bold active:scale-95 shadow-sm"
            >
              Confirm
            </button>
          </form>
        )}

        {/* 선택지 */}
        {showChoices && dialogue.choices && (
          <div
            className="mt-6 flex flex-col gap-2.5"
            onClick={(e) => e.stopPropagation()}
          >
            {dialogue.choices.map((choice, idx) => (
              <button
                key={idx}
                onClick={choice.action}
                className="w-full text-left px-5 py-3 bg-black/5 hover:bg-[#8b7355]/10 border border-[#d4c9b0] hover:border-[#8b7355] rounded-lg transition-all text-sm font-medium group/btn text-[#2a2a2a]"
              >
                <span className="opacity-40 mr-2 group-hover/btn:opacity-100 transition-opacity text-[#8b7355]">
                  →
                </span>
                {choice.label}
              </button>
            ))}
          </div>
        )}

        {/* 클릭 안내 */}
        {!isTyping && !showChoices && !showInput && (
          <div className="mt-4 flex items-center justify-end gap-2 opacity-40 text-[9px] font-bold tracking-tighter uppercase italic group-hover:opacity-60 transition-opacity text-[#8b7355]">
            Proceed
            <div className="w-1.5 h-1.5 bg-[#8b7355] rounded-full animate-bounce" />
          </div>
        )}
      </div>
    </Html>
  );
}
